import React, { useMemo, useContext, useCallback } from 'react';
import { CurrentPageContext, HistoryStackContext } from 'app/home/general-components/HistoryStackContext';
import { getBindIcons } from 'app/model/BindData';
import { useLocalizedStrings, LocalizationContext } from 'app/localization/Context';
import Toggle, { ToggleHeadIconType } from 'app/home/general-components/Toggle/Toggle';

import { ReactComponent as IconPlus } from 'images/general/icon-add.svg';
import { ReactComponent as IconDuration } from 'images/bind/duration.svg';
import { ReactComponent as IconDelay } from 'images/bind/delay.svg';
import { ReactComponent as IconArrow } from 'images/bind/arrow-right.svg';
import { ReactComponent as IconSelection } from 'images/general/icon-badge-select.svg';
import { ReactComponent as IconWwarningCircleWhite } from 'images/general/icon-warning-circle-white.svg';
import { ReactComponent as IconDayOfWeek } from 'images/bind/day-of-week.svg';

import './Binds.scss'

function Binds({ binds, devices, scenarios, currentUser, dataFetched, negotiatedProtocol }) {
    const hs = useContext(HistoryStackContext);

    const onCreateClick = useCallback(() => {
        hs.push("/binds/create_or_edit", {});
    }, [hs]);

    const s = useLocalizedStrings();

    return (
        <div className="bind-list-container">
            <div className="header">
                <div className="label">{s.binds}</div>
                <div />
                {
                    (dataFetched && negotiatedProtocol.hasMultiXBind && currentUser.permissions.bind.setting) ? (
                        <div className="button-border" onClick={onCreateClick}>
                            <div className="label">
                                {s.createBinds}
                            </div>
                            <IconPlus className="icon" />
                        </div>
                    ) : null
                }
            </div>

            {dataFetched ? (
                negotiatedProtocol.hasMultiXBind ?
                    <BindList
                        binds={binds} devices={devices} scenarios={scenarios}
                        cantChangeEnable={!currentUser.permissions.bind.change_enable} />
                    : <div className="bind-list">
                        <div className="empty-list-cell">{s.updateHubToUseBind}</div>
                    </div>
            ) : (
                <div className="bind-list">
                    <div className="empty-list-cell">{s.dataIsLoading}</div>
                </div>
            )}
        </div>
    );
}

export function BindList({
    binds, devices, scenarios, transparent, noBindText,
    selectionCallback, selectedBinds, cannotShowDetail, dontShowEnables,
    bottomPaddingSize, cantChangeEnable,
    style }) {
    const p = useContext(CurrentPageContext);
    const s = useLocalizedStrings();

    const _noBindText = noBindText ? noBindText : s.noBind;

    const _bottomPaddingSize = bottomPaddingSize ? bottomPaddingSize : 16;

    const _transparent = (transparent !== undefined) ? transparent : false;

    const bindElementsWithBlank = useMemo(() => {
        const _selectedBinds = Array.isArray(selectedBinds) ? selectedBinds : [];

        let elements = [];
        for (let i = 0; i < binds.length; i++) {
            const bind = binds[i];
            elements.push(
                <BindCell key={"bind_id_" + String(bind.id)}
                    bind={bind} devices={devices} scenarios={scenarios} transparent={_transparent}
                    selectionCallback={selectionCallback}
                    dontShowEnables={dontShowEnables} _cantChangeEnable={cantChangeEnable}
                    cannotShowDetail={cannotShowDetail}
                    isSelected={_selectedBinds.includes(bind.id)} />
            )
        }

        elements.push(
            <div style={{ height: _bottomPaddingSize - 9 }} key="bind-list-padding" />
        );

        return elements;
    }, [binds, selectionCallback, cannotShowDetail, dontShowEnables,
        selectedBinds, _bottomPaddingSize, _transparent, devices, scenarios,
        cantChangeEnable])

    return (
        <div className={"bind-list " + (p.inOverlayPage ? "" : "responsive")} style={style}>
            {
                bindElementsWithBlank.length > 1 ? bindElementsWithBlank :
                    <div className="empty-list-cell"
                        style={{ opacity: !transparent ? 1 : 0.5 }}>{_noBindText}</div>
            }
        </div>
    );
}

function BindCell({
    bind, devices, scenarios, transparent, selectionCallback,
    dontShowEnables, _cantChangeEnable, cannotShowDetail, isSelected }) {
    const l = useContext(LocalizationContext);

    // const [sourceEventIcon, destinationEventIcon] =
    //     useMemo(() => {
    //         const sourceEvent = getSourceEventData(bind, l, devices);
    //         const destinationEvent = getDestinationEventData(bind, l, devices);

    //         return [sourceEvent[0], destinationEvent[0]]
    //     }, [bind, l, devices]);

    const onEnableClick = useCallback(() => {
        if (selectionCallback) {
            selectionCallback(bind);
        } else {
            bind.setEnable(!bind.enable)
        }
    }, [bind, selectionCallback]);

    const hs = useContext(HistoryStackContext);

    const onDetailClick = useCallback(() => {
        if (selectionCallback) {
            selectionCallback(bind);
        } else if (!cannotShowDetail) {
            hs.push("/binds/detail", bind.id);
        }
    }, [hs, bind, selectionCallback, cannotShowDetail]);

    const selectionOnClick = useCallback(() => {
        if (selectionCallback)
            selectionCallback(bind);
    }, [selectionCallback, bind]);

    const showToggleLoading = (bind.waitingForChangeEnable && !bind.waitingForChangeEnable.ignoreWaiting);
    
    const numberOfSelectedDays = useMemo(() => {
        if (bind.day_of_week !== undefined)
            return bind.day_of_week.filter(v => v).length;
        else
            return 7;
    }, [bind]);

    const [haveError, conditionIcons, actionIcons] = useMemo(() => {
        let [_conds, _acts] = getBindIcons(bind, devices, scenarios, l);
        let _emptyCondOrAct = (_conds.length === 0) || (_acts.length === 0);

        if (_conds.length === 0) {
            _conds = [IconWwarningCircleWhite];
        }
        if (_acts.length === 0) {
            _acts = [IconWwarningCircleWhite];
        }
        const _haveError = _emptyCondOrAct || (bind.participates_in_cycle === true) || (numberOfSelectedDays === 0);

        return [_haveError, _conds, _acts];
    }, [bind, numberOfSelectedDays, devices, scenarios, l]);

    const cantChangeEnable = _cantChangeEnable || haveError;
    // console.log(bind.name, _cantChangeEnable, haveError, cantChangeEnable);

    return (
        <div className={haveError ? "bind-cell have-error" : "bind-cell"}
            style={{ opacity: !transparent ? 1 : 0.7 }}>
            <div className="bind-cell-name-and-icons-group">
                <div className="bind-text">
                    {bind.name}
                    {isSelected ? " " : ""}
                    {isSelected &&
                        <IconSelection className="selection-tick" onClick={selectionOnClick} />}
                </div>

                <div className="icons">
                    {conditionIcons.map((icon, index) =>
                        React.createElement(icon, {
                            className: "bind-device-icon",
                            key: "bind-cell-id-" + String(bind.id) + "-cond-icons-" + String(index)
                        }))}
                    <IconArrow className="bind-device-icon arrow" />
                    {actionIcons.map((icon, index) =>
                        React.createElement(icon, {
                            className: "bind-device-icon",
                            key: "bind-cell-id-" + String(bind.id) + "-act-icons-" + String(index)
                        }))}
                </div>
            </div>

            <div /> {/* space */}

            <div className={isSelected ? "selection-panel active" : "selection-panel"}
                onClick={selectionOnClick} /> {/* blue screen */}

            <div className="bind-cell-status-group">
                <div className="duration-and-delay">
                    {(bind.delay > 0) ?
                        <IconDelay className="icon" /> : null}
                    {bind.isDurationSpecified ?
                        <IconDuration className="icon" /> : null}
                    {(numberOfSelectedDays !== 7) ?
                        <IconDayOfWeek className="icon" /> : null}
                </div>
                {
                    (!dontShowEnables) ?
                        <Toggle
                            unclickable={cantChangeEnable}
                            onClick={onEnableClick}
                            customHeadIconType={
                                showToggleLoading ? ToggleHeadIconType.loading : (
                                    haveError ? (ToggleHeadIconType.error) : (
                                        cantChangeEnable ? ToggleHeadIconType.lock : ToggleHeadIconType.checkOrX
                                    )
                                )
                            }
                            enable={bind.enable && !haveError} /> : null
                }
            </div>

            <div onClick={onDetailClick} className="detail-click-overlay" />

        </div>
    )
}

export default Binds;
